/**
 * Per-device UI preferences — DESIGN.md §13 (Long-Day Review, Tier 1).
 *
 * Small, local, and boring on purpose: sidebar collapsed, card-panel width,
 * text size. These are *device* preferences (a laptop and a 32" monitor want
 * different answers), so they live in localStorage, not on the user record.
 *
 * Every key is namespaced `slate.ui.*`. Reads and writes are wrapped because
 * private browsing and blocked site data throw on access rather than returning
 * null — neither is a reason to fail to render.
 */
import { useCallback, useEffect, useState } from 'react';

export const PREF_KEYS = {
  sidebarCollapsed: 'slate.ui.sidebar-collapsed',
  panelWidth: 'slate.ui.panel-width',
  textSize: 'slate.ui.text-size',
  viewTypeByProject: 'slate.ui.view-type', // + `.${projectKey}`
  quickAddSection: 'slate.ui.quick-add-section', // + `.${projectKey}`
  viewTweaks: 'slate.ui.view-tweaks', // + `.${projectKey}.${viewId ?? 'default'}`
} as const;

export function readPref<T>(key: string, parse: (raw: string) => T | undefined): T | undefined {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return undefined;
    return parse(raw);
  } catch {
    return undefined;
  }
}

export function writePref(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // Not persisted; the session still honours the choice.
  }
  // Same-tab listeners (the `storage` event only fires in *other* tabs).
  window.dispatchEvent(new CustomEvent(PREF_EVENT, { detail: { key } }));
}

const PREF_EVENT = 'slate:pref';

/**
 * `useState` that survives reload. `parse` turns the stored string back into a
 * value and returns `undefined` for anything it does not recognise, so a stale
 * or hand-edited entry falls back to the default rather than crashing.
 *
 * Two components reading the same key stay in sync within the tab and across
 * tabs.
 */
export function usePersistedState<T>(
  key: string,
  fallback: T,
  parse: (raw: string) => T | undefined,
  serialize: (value: T) => string = (value) => String(value),
): [T, (next: T | ((current: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => readPref(key, parse) ?? fallback);

  useEffect(() => {
    const sync = (event: Event): void => {
      const changed =
        event instanceof StorageEvent
          ? event.key
          : (event as CustomEvent<{ key: string }>).detail?.key;
      if (changed !== null && changed !== undefined && changed !== key) return;
      setValue(readPref(key, parse) ?? fallback);
    };
    window.addEventListener('storage', sync);
    window.addEventListener(PREF_EVENT, sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener(PREF_EVENT, sync);
    };
    // `parse`/`fallback` are expected to be stable module-level values.
  }, [key]);

  const set = useCallback(
    (next: T | ((current: T) => T)) => {
      setValue((current) => {
        const resolved = typeof next === 'function' ? (next as (c: T) => T)(current) : next;
        writePref(key, serialize(resolved));
        return resolved;
      });
    },
    [key, serialize],
  );

  return [value, set];
}

export const parseBoolean = (raw: string): boolean | undefined =>
  raw === '1' ? true : raw === '0' ? false : undefined;
export const serializeBoolean = (value: boolean): string => (value ? '1' : '0');

export const parseNumber = (raw: string): number | undefined => {
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
};

/* ------------------------------------------------------------------------ */
/* Text size                                                                 */
/* ------------------------------------------------------------------------ */

/**
 * Root font-size steps. Everything in the app is sized in rem, so one attribute
 * on <html> scales the whole UI — for people who would otherwise live at 125%
 * browser zoom. Applied pre-paint by the inline script in index.html and kept
 * in sync by `useTextSize`; the two MUST agree on key, names and pixel values.
 */
export const TEXT_SIZES = ['sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl'] as const;
export type TextSize = (typeof TEXT_SIZES)[number];
export const TEXT_SIZE_LABELS: Record<TextSize, string> = {
  sm: 'Compact',
  md: 'Default',
  lg: 'Large',
  xl: 'Larger',
  '2xl': 'Larger still',
  '3xl': 'Huge',
  '4xl': 'Maximum',
};
/**
 * The top of the scale is set by what it replaces (#31): browser zoom. 20px
 * was only 125% of the default, which is why zoom was still needed — 24, 27
 * and 30 reach roughly 150%, 170% and 190%, so the browser's own control
 * should no longer be the better option.
 */
export const TEXT_SIZE_PX: Record<TextSize, number> = {
  sm: 15,
  md: 16,
  lg: 18,
  xl: 20,
  '2xl': 24,
  '3xl': 27,
  '4xl': 30,
};

const parseTextSize = (raw: string): TextSize | undefined =>
  (TEXT_SIZES as readonly string[]).includes(raw) ? (raw as TextSize) : undefined;

export function applyTextSize(size: TextSize): void {
  document.documentElement.dataset['text'] = size;
}

export function useTextSize(): [TextSize, (next: TextSize) => void] {
  const [size, setSize] = usePersistedState<TextSize>(PREF_KEYS.textSize, 'md', parseTextSize);
  useEffect(() => applyTextSize(size), [size]);
  return [size, setSize];
}
